import React from 'react';
import Utils from '../shared/Utils';
import LoadingCube from '../shared/LoadingCube';
import ResultsSwitcher from './Switcher';
import ResultsTable from './Table';
import ProfessorHelpers from '../professors/ProfessorHelpers';

// ---- Beginning of Helper React components ----
const EditModeActions = ({ is_admin, is_preview, edit_mode, handleSave, toggleEditMode }) => {
  if (!is_admin || is_preview) return null;
  const editTextKey = edit_mode ? 'stop_edit' : 'edit';
  const toggleClass = edit_mode ? 'btn-secondary' : 'btn-primary';

  return (
    <div className='row text-center'>
      <div className='col'>
        {/* Save Button */}
        {edit_mode && (
          <div
            className='btn-primary btn text-center mr-5'
            onClick={handleSave}
            children={Utils.t('basic.save')}
          />
        )}

        {/* Toggle Edit Mode Button */}
        <div
          className={`toggle-edit-mode ${toggleClass} btn text-center`}
          onClick={toggleEditMode}
          children={Utils.t(`basic.${editTextKey}`)}
        />
      </div>
    </div>
  );
};

const TeamResults = ({ edit_mode, active_team_results, determineHeader }) => {
  if (edit_mode) return null;

  return (
    <React.Fragment>
      <h4 className='blue-text space-bottom' children={determineHeader('teams')} />
      <ResultsTable type='teams' results={active_team_results} />
    </React.Fragment>
  );
}

const ActualResults = (props) => {
  const {
    fetching,
    results_exist,
    active_year,
    active_term_as_int,
    active_individual_results,
    determineHeader
  } = props;
  if (fetching) {
    return <LoadingCube />;
  }

  const subtitleClass = 'text-center grey-text push-small';
  const resultsUnreleased = Utils.t('results.no_results_released', {
    year: active_year,
    term: Utils.getTerm(active_term_as_int)
  });
  const resultsExistForCurrentSelection = active_individual_results.length > 0;
  const noResults = Utils.t('results.no_results_exist');

  return (
    <div className='container'>
      <EditModeActions {...props} />

      {!results_exist && <h4 className={subtitleClass} children={resultsUnreleased} />}
      {!resultsExistForCurrentSelection && <h4 className={subtitleClass} children={noResults} />}
      {results_exist && resultsExistForCurrentSelection && (
        <React.Fragment>
          <h4 className='blue-text space-bottom' children={determineHeader('individuals')} />
          <ResultsTable
            {...props}
            type='individuals'
            results={active_individual_results}
          />
          <TeamResults {...props} />
        </React.Fragment>
      )}
    </div>
  );
};
// ---- End of Helper React components ----

class Results extends React.PureComponent {
  state = {
    fetching: false,
    loaded: false
  };

  formQueryString = () => {
    const {
      loaded,
      count_indiv,
      count_team,
      active_exam_level,
      active_term_as_int,
      active_year,
      is_preview
    } = this.state;
    const initialData = this.props.data;
    let result = `count_indiv=${loaded ? count_indiv : initialData.countIndiv}`;
    result += `&count_team=${loaded ? count_team : initialData.countTeam}`;
    result += `&exam_level=${loaded ? active_exam_level : initialData.examLevel}`;
    result += `&is_preview=${loaded ? is_preview : initialData.isPreview}`;
    result += `&term_as_int=${loaded ? active_term_as_int : initialData.termAsInt}`;
    result += `&year=${loaded ? active_year : initialData.year}`;

    return result;
  }

  loadResults = () => {
    window.reactFinishedRendering = false;
    const url = `/home/results_data?${this.formQueryString()}`;
    const successCb = (data) => {
      window.reactFinishedRendering = true;
      this.setState(data);
      if (data.is_preview) {
        const queryString = `count_indiv=${data.count_indiv}&count_team=${data.count_team}&exam_level=${data.active_exam_level}`;
        Utils.setPageUrl('/admin/preview_current_results?', queryString, Utils.t('titles.results_preview'));
      }
    };

    const errorCb = () => {
      window.reactFinishedRendering = true;
      Utils.showNotification(Utils.t('results.load_failed'), 'error');
    };

    Utils.loadInitialReactData(url, successCb, errorCb);
  }

  componentDidMount() {
    this.loadResults();
  }

  changeExam = (index) => {
    const { is_preview, active_year, active_term_as_int } = this.state;

    this.setState({ active_exam_level: index, fetching: true }, () => {
      const baseUrl = is_preview ? '/admin/preview_current_results' : '/home/results';
      const params = `?year=${active_year}&term_as_int=${active_term_as_int}&exam_level=${index}`;
      Utils.setPageUrl(baseUrl, params, 'competition results');
      this.loadResults();
    });
  }

  changeCount = (e, type) => {
    this.setState({ [`${type}`]: e.target.value, fetching: true }, () => {
      this.loadResults();
    });
  }

  determineHeader = (type) => {
    const { is_preview, count_indiv, count_team } = this.state;
    const count = (type === 'individuals') ? count_indiv : count_team;
    const ending = is_preview ? '_preview' : '';

    return Utils.t(`results.top_${type}${ending}`, { count: count });
  }

  toggleEditMode = () => {
    const { edit_mode, previous_results, active_individual_results } = this.state;
    if (edit_mode) {
      // CASE: we are turning edit mode off. We need to revert to the previous state
      this.setState({ edit_mode: false, active_individual_results: previous_results });
    } else {
      // CASE: we are turning edit mode on. We need to save the current state
      this.setState({ edit_mode: true, previous_results: [ ...active_individual_results ] });
    }
  }

  handleChange = (id, newScore) => {
    const { active_individual_results } = this.state;
    const newResults = active_individual_results.map((result) => {
      return result.id !== id ? result : { ...result, score: newScore }
    });

    this.setState({ active_individual_results: newResults });
  }

  handleSave = () => {
    const results = this.state.active_individual_results.map(result => {
      return { id: result.id, score: result.score }
    });
    ProfessorHelpers.saveHistoricalRecords(results, () => this.setState({ fetching: false }));

    this.setState({ fetching: true });
  }

  render() {
    const { loaded } = this.state;
    if (!loaded) return null;

    return (
      <div className='fade-in-element'>
        <ResultsSwitcher
          {...this.state}
          changeExam={this.changeExam}
          changeCount={this.changeCount}
        />

        <ActualResults
          {...this.state}
          determineHeader={this.determineHeader}
          handleChange={this.handleChange}
          handleSave={this.handleSave}
          toggleEditMode={this.toggleEditMode}
        />
      </div>
    );
  }
}

export default Results;
